<template>
  <div v-if="canSeeNotificationCenter" data-testid="page-notification-center">
    <div class="flex flex-wrap items-center justify-between gap-2">
      <h1>{{ t('NotificationCenter.title') }}</h1>
      <button
        v-if="showMarkAllAsRead"
        type="button"
        class="btn-outline-neutral-xs"
        data-testid="notification-center-mark-all-read"
        :disabled="markingAll"
        @click="markAllAsRead"
      >
        {{ t('NotificationCenter.markAllAsRead') }}
        <Loader2Icon v-if="markingAll" class="lucide-icon-s animate-spin" />
        <MailOpenIcon v-else class="lucide-icon-s" />
      </button>
    </div>

    <div class="mt-6 border-b border-main">
      <TabBar>
        <TabBarItem
          v-for="option in TAB_OPTIONS"
          :key="option"
          :model-value="tab"
          :selected-value="option"
          :data-testid="`notification-center-tab-${option}`"
          @update:model-value="handleTabChange"
        >
          {{ t(`NotificationCenter.tabs.${option}`) }}
        </TabBarItem>
      </TabBar>
    </div>

    <div class="mt-4 flex gap-2">
      <NotificationCenterCategoryFilter level="product" :options="productOptions" :selected="product" @select="setProduct" />
      <NotificationCenterCategoryFilter level="topic" :options="topicOptions" :selected="topic" @select="setTopic" />
    </div>

    <div class="mt-6 flex flex-col gap-3">
      <div v-if="!loading && !notifications.length" class="rounded-2xl border border-main bg-surface">
        <NotificationEmptyState
          :title="t(`NotificationCenter.emptyState.${tab}.title`)"
          :body="t(`NotificationCenter.emptyState.${tab}.body`)"
        />
      </div>

      <NotificationRowTransition>
        <div v-for="row in rows" :key="row.key">
          <h2 v-if="row.type === 'bucket'" class="txt-label-s-medium text-subtlest" data-testid="notification-center-bucket-header">
            {{ t(`Dashboard.notifications.buckets.${row.bucketKey}`) }}
          </h2>
          <NotificationCard
            v-else
            :notification="row.notification"
            show-read-toggle
            :show-restore="isArchivedTab"
            @cta-click="handleCtaClick"
            @toggle-read="toggleRead"
            @archive="archiveOne"
            @restore="restoreOne"
          />
        </div>
      </NotificationRowTransition>

      <template v-if="loading || loadingMore">
        <NotificationCardSkeleton v-for="i in SKELETON_COUNT" :key="`skeleton-${i}`" />
      </template>

      <div ref="sentinel" data-testid="notification-center-sentinel" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Loader2Icon, MailOpenIcon } from '@ramp106/omrjs-core-ui/icons'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { useIntersectionObserver } from '@vueuse/core'
import { computed, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'

import NotificationCard from '@/components/Dashboard/NotificationCard/NotificationCard.vue'
import NotificationCardSkeleton from '@/components/Dashboard/NotificationFeed/NotificationCardSkeleton.vue'
import NotificationEmptyState from '@/components/Dashboard/NotificationFeed/NotificationEmptyState.vue'
import { notificationRows } from '@/components/Dashboard/NotificationFeed/notificationRows'
import NotificationRowTransition from '@/components/Dashboard/NotificationFeed/NotificationRowTransition.vue'
import {
  categoriesUnder,
  categoryPrefixes,
  PRODUCT_SEGMENTS,
  TOPIC_SEGMENTS,
} from '@/components/NotificationCenter/notificationCategoryPrefix'
import NotificationCenterCategoryFilter from '@/components/NotificationCenter/NotificationCenterCategoryFilter.vue'
import { NotificationCenterTab } from '@/components/NotificationCenter/notificationCenterTabFromQuery'
import { useRouterQueryNotificationCenterCategory } from '@/components/NotificationCenter/useRouterQueryNotificationCenterCategory'
import { useRouterQueryNotificationCenterTab } from '@/components/NotificationCenter/useRouterQueryNotificationCenterTab'
import { FEATURE_FLAG } from '@/composables/featureFlagCatalog'
import { useFeatureFlag } from '@/composables/useFeatureFlag'
import { useNotificationCenterPage } from '@/composables/useNotificationCenterPage'
import { checkEnumValue } from '@/helpers/checkEnumValue'
import { useRoleRedirect } from '@/services/roleRedirect'
import { $can } from '@/services/roles'
import { TabBar, TabBarItem } from '@/ui/Tabs'

const SKELETON_COUNT = 3
const TAB_OPTIONS = Object.values(NotificationCenterTab)

const { t } = useI18n()

useRoleRedirect('manageReviews')

const { enabled: isCenterEnabled } = useFeatureFlag(FEATURE_FLAG.NOTIFICATIONS.CENTER_RELEASE)
const canSeeNotificationCenter = computed(() => isCenterEnabled.value && $can('manageReviews', { for: 'User' }))

const { tab, setTab } = useRouterQueryNotificationCenterTab()
const { product, topic, categoryPrefix, setProduct, setTopic } = useRouterQueryNotificationCenterCategory()
const {
  notifications,
  availableCategories,
  pageInfo,
  loading,
  loadingMore,
  markingAll,
  unreadCount,
  loadMore,
  toggleRead,
  archiveOne,
  restoreOne,
  markAllAsRead,
  handleCtaClick,
} = useNotificationCenterPage(tab, categoryPrefix)

const productOptions = computed(() => categoryPrefixes(availableCategories.value, PRODUCT_SEGMENTS))
const topicOptions = computed(() => categoryPrefixes(categoriesUnder(availableCategories.value, product.value), TOPIC_SEGMENTS))

const isArchivedTab = computed(() => tab.value === NotificationCenterTab.ARCHIVED)
const showMarkAllAsRead = computed(() => !isArchivedTab.value && unreadCount.value > 0)
const rows = computed(() => notificationRows(notifications.value))

function handleTabChange(value: string) {
  const nextTab = checkEnumValue(value, NotificationCenterTab, NotificationCenterTab.ALL)
  if (nextTab === tab.value) return
  track('filter_apply', {
    surface: 'filter',
    filter_group: 'manage_notifications',
    filter_type: 'button',
    filter_name: 'tab',
    filter_value: nextTab,
  })
  setTab(nextTab)
}

const sentinel = useTemplateRef<HTMLDivElement>('sentinel')

useIntersectionObserver(sentinel, ([entry]) => {
  if (!entry.isIntersecting) return
  if (!pageInfo.value.hasNextPage) return
  if (loading.value || loadingMore.value) return
  loadMore()
})
</script>
